import React, { useEffect } from 'react';

const BuilderPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Builder Pattern - Creational Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Builder Pattern - construct complex objects step by step with flexible, readable code. Learn with computer builder and pizza examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Builder Pattern - construct complex objects step by step with flexible, readable code. Learn with computer builder and pizza examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'builder pattern, design patterns, creational patterns, object construction, fluent interface, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'builder pattern, design patterns, creational patterns, object construction, fluent interface, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Builder Pattern</h1>
        <p style={styles.subtitle}>
          Creational Design Pattern - Construct complex objects step by step
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Builder pattern allows you to construct complex objects step by step. It separates 
            the construction of a complex object from its representation, allowing the same construction 
            process to create different representations.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            Imagine creating a complex object like a House with many optional parameters: walls, roof, 
            windows, doors, garage, pool, garden, etc. Using constructors would result in telescoping 
            constructor anti-pattern with many parameters.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Computer Builder</h2>
          <div style={styles.codeBlock}>
{`// Product
class Computer {
    private String cpu;
    private String ram;
    private String storage;
    private String gpu;
    private String motherboard;
    private String powerSupply;
    private String coolingSystem;
    private boolean hasWifi;
    private boolean hasBluetooth;
    
    // Private constructor - only builder can create
    private Computer(ComputerBuilder builder) {
        this.cpu = builder.cpu;
        this.ram = builder.ram;
        this.storage = builder.storage;
        this.gpu = builder.gpu;
        this.motherboard = builder.motherboard;
        this.powerSupply = builder.powerSupply;
        this.coolingSystem = builder.coolingSystem;
        this.hasWifi = builder.hasWifi;
        this.hasBluetooth = builder.hasBluetooth;
    }
    
    // Static nested builder class
    public static class ComputerBuilder {
        // Required parameters
        private String cpu;
        private String ram;
        private String storage;
        
        // Optional parameters with default values
        private String gpu = "Integrated";
        private String motherboard = "Standard";
        private String powerSupply = "500W";
        private String coolingSystem = "Stock Cooler";
        private boolean hasWifi = false;
        private boolean hasBluetooth = false;
        
        // Constructor with required parameters
        public ComputerBuilder(String cpu, String ram, String storage) {
            this.cpu = cpu;
            this.ram = ram;
            this.storage = storage;
        }
        
        // Fluent interface methods for optional parameters
        public ComputerBuilder gpu(String gpu) {
            this.gpu = gpu;
            return this;
        }
        
        public ComputerBuilder motherboard(String motherboard) {
            this.motherboard = motherboard;
            return this;
        }
        
        public ComputerBuilder powerSupply(String powerSupply) {
            this.powerSupply = powerSupply;
            return this;
        }
        
        public ComputerBuilder coolingSystem(String coolingSystem) {
            this.coolingSystem = coolingSystem;
            return this;
        }
        
        public ComputerBuilder withWifi() {
            this.hasWifi = true;
            return this;
        }
        
        public ComputerBuilder withBluetooth() {
            this.hasBluetooth = true;
            return this;
        }
        
        // Build method
        public Computer build() {
            return new Computer(this);
        }
    }
}

// Usage
public class BuilderDemo {
    public static void main(String[] args) {
        // Basic computer
        Computer basicComputer = new Computer.ComputerBuilder(
                "Intel i5", "8GB", "256GB SSD")
                .build();
        
        // Gaming computer
        Computer gamingComputer = new Computer.ComputerBuilder(
                "Intel i9", "32GB", "1TB SSD")
                .gpu("RTX 4080")
                .motherboard("Gaming Motherboard")
                .powerSupply("850W")
                .coolingSystem("Liquid Cooling")
                .withWifi()
                .withBluetooth()
                .build();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Flexible Construction:</strong> Step-by-step object construction</li>
            <li style={styles.listItem}><strong>Readable Code:</strong> Fluent interface makes code self-documenting</li>
            <li style={styles.listItem}><strong>Immutable Objects:</strong> Can create immutable objects easily</li>
            <li style={styles.listItem}><strong>Parameter Validation:</strong> Can validate parameters during construction</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When creating complex objects with many optional parameters</li>
            <li style={styles.listItem}>When you want to avoid telescoping constructors</li>
            <li style={styles.listItem}>When you need different representations of the same object</li>
            <li style={styles.listItem}>When you want to create immutable objects</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Builder pattern provides a clean way to construct complex objects with many parameters. 
            It improves code readability and maintainability while providing flexibility in object creation.
          </p>
        </section>
      </main>
    </article>
  );
};

export default BuilderPattern;